import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { toast } from 'sonner';
import { Fish, Eye, EyeSlash } from '@phosphor-icons/react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';

const AdminLoginPage = () => {
  const navigate = useNavigate();
  const { login, setupAdmin, checkAdminExists, isAuthenticated, loading, error } = useAuth();
  const { t } = useLanguage();
  
  const [isSetup, setIsSetup] = useState(false);
  const [checkingAdmin, setCheckingAdmin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/admin/dashboard');
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    const checkAdmin = async () => {
      const exists = await checkAdminExists();
      setIsSetup(!exists);
      setCheckingAdmin(false);
    };
    checkAdmin();
  }, [checkAdminExists]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (isSetup) {
      if (password !== confirmPassword) {
        toast.error('Les mots de passe ne correspondent pas');
        return;
      }
      if (password.length < 6) {
        toast.error('Le mot de passe doit contenir au moins 6 caractères');
        return;
      }
      const result = await setupAdmin(email, password);
      if (result.success) {
        toast.success('Compte admin créé avec succès');
        navigate('/admin/dashboard');
      } else {
        toast.error(result.error);
      }
    } else {
      const result = await login(email, password);
      if (result.success) {
        toast.success('Connexion réussie');
        navigate('/admin/dashboard');
      } else {
        toast.error(result.error);
      }
    }
  };

  if (checkingAdmin) {
    return (
      <div className="min-h-screen bg-[#000000] flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#000000] flex items-center justify-center px-4" data-testid="admin-login-page">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="w-16 h-16 rounded-full bg-[#b7ede0]/10 flex items-center justify-center mx-auto mb-4">
            <Fish size={32} weight="fill" className="text-[#b7ede0]" />
          </div>
          <CardTitle className="text-2xl font-['Playfair_Display']">
            {isSetup ? 'Créer un compte Admin' : 'Admin Calvi Fishing'}
          </CardTitle>
          <CardDescription>
            {isSetup 
              ? 'Créez votre compte administrateur pour gérer les réservations'
              : 'Connectez-vous pour accéder au tableau de bord'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@calvi-fishing.com"
                required
                data-testid="admin-email-input"
              />
            </div>
            
            <div>
              <Label htmlFor="password">Mot de passe</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  data-testid="admin-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#4B5563] hover:text-[#000000]"
                >
                  {showPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            {isSetup && (
              <div>
                <Label htmlFor="confirmPassword">Confirmer le mot de passe</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  data-testid="admin-confirm-password-input"
                />
              </div>
            )}

            {error && (
              <p className="text-sm text-[#FF3B30]">{error}</p>
            )}

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#b7ede0] hover:bg-[#9ee5d4] text-white rounded-full"
              data-testid="admin-login-btn"
            >
              {loading ? (
                <div className="spinner w-5 h-5" />
              ) : isSetup ? (
                'Créer le compte'
              ) : (
                'Se connecter'
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminLoginPage;
